/* ==========================================================================
 * svgLabelPreview — imperative bridge between a preview <svg> node and the shared
 * svgLabelBadge module.
 *
 * Ported from product-labels-2.0-frontend/src/utility/Utils.jsx (`buildDecoLayout` /
 * `applySvgLabelPreview`). Keep the two in step: a divergence here means the support agent
 * and the merchant are looking at different drawings of the same record.
 * See .claude/features/svg_label_and_badge.md §5
 * ========================================================================== */

import { applyAspect, layoutSvgNode, nextDecoUid, scheduleSvgLayout } from "./svgLabelBadge";
import { loadFont } from "./Ultility";

/**
 * Normalises a label/badge record + its design row into the `__decoLayout` config the shared
 * module draws from. Field names mirror the storefront's own builder so both render the same.
 */
export const buildDecoLayout = (
  {
    data = {},
    design = {},
    type = "label",
    page = "collection",
    isMobile = false,
    text = "",
    width,
    height,
  },
  extra = {}
) => {
  // Inline HTML is handed over RAW: the shared module parses it into styled runs (see
  // svgLabelBadge `parseRichText`), including entity decoding. Pre-escaping here is what would
  // make this preview disagree with the storefront.
  const resolvedText = String(text ?? "");

  return {
    type,
    id: data.id,
    uid: extra.uid ?? nextDecoUid(),
    page,
    isMobile,
    widthTmd: width,
    heightTmd: height,
    design,
    hasImage: !!data.image,
    image: data.image,
    ratioSize: !Object.prototype.hasOwnProperty.call(data, "ratio_size") || data.ratio_size,
    alt: data.alt || data.name || "",
    shapeClass: data.image ? null : design.shape_class,
    textType: data[`${type}_text_type`] ?? data.text_type,
    shapeColor: design.shape_color,
    gradientDir: design.shape_gradient_direction,
    startColor: design.shape_start_color,
    endColor: design.shape_ending_color,
    border: design.border || null,
    borderRadius: design.border_radius || 0,
    text: resolvedText,
    customTextCss: data.text_styles || "",
    // Container CSS: inheritable text props act as the lowest cascade layer (see module).
    customCss: data.styles || "",
    imageAspect: extra.imageAspect,
    link: null,
  };
};

/**
 * `node` must be an <svg> element. Safe to call on every render: when nothing that affects the
 * drawing changed, it re-applies the CSS aspect-ratio and returns without touching the children.
 */
export const applySvgLabelPreview = (node, cfg) => {
  if (!node) return;
  const { design = {} } = cfg;

  if (design.font_family && design.font_family !== "default") loadFont(design.font_family);

  const prev = node.__decoLayout;
  const nextLayout = buildDecoLayout(cfg);

  // Carry over the aspect the image probe already learned. Dropping it makes effectiveAspect fall
  // back to the width/height ratio and scheduleSvgLayout probe the image again, so after every edit
  // the box snaps to the wrong ratio and then redraws a second time when the probe resolves.
  if (prev && prev.image === nextLayout.image && prev.imageAspect) {
    nextLayout.imageAspect = prev.imageAspect;
  }

  // The preview effect re-runs on every keystroke, including on edits that cannot change the
  // drawing at all. Redrawing then is pure cost, and for an image label it is visible: the <image>
  // and the gradient/clip ids get rebuilt for nothing. Built from the WHOLE config minus the
  // derived fields, deliberately: enumerating the fields that matter is how a preview ends up
  // silently stale. Extra fields only cost an extra redraw.
  const signature = JSON.stringify({ ...nextLayout, uid: 0, viewW: 0, viewH: 0, imageAspect: 0 });
  if (prev && node.__decoSig === signature) {
    // Callers strip the inline style immediately before calling this (`removeAttribute("style")`),
    // so the aspect-ratio has to be written back even when the redraw is skipped — without it the
    // box loses its ratio entirely. Keep the OLD config: it holds the probed imageAspect and the
    // uid that the existing children's gradient/clip ids were built from.
    applyAspect(node, prev);
    return;
  }

  node.__decoLayout = nextLayout;
  node.__decoSig = signature;

  // Derives the viewBox and the CSS aspect-ratio from one source so they cannot drift apart.
  applyAspect(node, node.__decoLayout);
  layoutSvgNode(node);
  scheduleSvgLayout(node);
};
